/**
 * Release Schedule Settings
 *
 * Component for configuring Release QA schedule settings.
 * Supports simple (daily), cron, and interval schedule types.
 */

import React from 'react';
import { Checkbox, Select, TextField, NumberField, Text, Flex, Box } from '@chatsdet/ui';
import { useValidateCron } from '../useReleases';
import type {
  ScheduleSettings,
  SimpleScheduleConfig,
  CronScheduleConfig,
  IntervalScheduleConfig,
} from '../releaseTypes';

type ScheduleType = 'simple' | 'cron' | 'interval';

type ReleaseScheduleSettingsProps = {
  value: ScheduleSettings | null;
  onChange: (value: ScheduleSettings | null) => void;
};

const SCHEDULE_TYPE_OPTIONS = [
  { id: 'simple', label: 'Daily at specific time' },
  { id: 'cron', label: 'Custom cron expression' },
  { id: 'interval', label: 'Fixed interval' },
];

const COMMON_TIMEZONES = [
  { id: 'America/New_York', label: 'Eastern (New York)' },
  { id: 'America/Chicago', label: 'Central (Chicago)' },
  { id: 'America/Denver', label: 'Mountain (Denver)' },
  { id: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { id: 'America/Sao_Paulo', label: 'Sao Paulo' },
  { id: 'Europe/London', label: 'London' },
  { id: 'Europe/Paris', label: 'Paris' },
  { id: 'Europe/Berlin', label: 'Berlin' },
  { id: 'Asia/Tokyo', label: 'Tokyo' },
  { id: 'Asia/Shanghai', label: 'Shanghai' },
  { id: 'Asia/Singapore', label: 'Singapore' },
  { id: 'Asia/Kolkata', label: 'India (Kolkata)' },
  { id: 'Australia/Sydney', label: 'Sydney' },
  { id: 'UTC', label: 'UTC' },
];

const DEFAULT_SCHEDULE: ScheduleSettings = {
  enabled: true,
  scheduleType: 'simple',
  scheduleConfig: {
    time: '09:00',
    timezone: 'America/New_York',
  },
};

function getDefaultConfigForType(type: ScheduleType): ScheduleSettings['scheduleConfig'] {
  switch (type) {
    case 'simple':
      return { time: '09:00', timezone: 'America/New_York' };
    case 'cron':
      return { pattern: '0 9 * * *', timezone: 'America/New_York' };
    case 'interval':
      return { intervalMs: 3600000 }; // 1 hour
  }
}

export const ReleaseScheduleSettings: React.FC<ReleaseScheduleSettingsProps> = ({
  value,
  onChange,
}) => {
  const validateCron = useValidateCron();
  const [cronError, setCronError] = React.useState<string | null>(null);
  const [cronNextRuns, setCronNextRuns] = React.useState<string[]>([]);

  const isEnabled = value?.enabled ?? false;
  const scheduleType = value?.scheduleType ?? 'simple';
  const scheduleConfig = value?.scheduleConfig;

  // Validate cron pattern on change
  React.useEffect(() => {
    if (scheduleType === 'cron' && scheduleConfig && 'pattern' in scheduleConfig) {
      const cronConfig = scheduleConfig as CronScheduleConfig;
      const debounceTimer = setTimeout(() => {
        validateCron.mutate(
          { pattern: cronConfig.pattern, timezone: cronConfig.timezone },
          {
            onSuccess: (result) => {
              if (result.valid) {
                setCronError(null);
                setCronNextRuns(result.nextRuns ?? []);
              } else {
                setCronError(result.error ?? 'Invalid cron pattern');
                setCronNextRuns([]);
              }
            },
            onError: () => {
              setCronError('Failed to validate cron pattern');
              setCronNextRuns([]);
            },
          }
        );
      }, 500);

      return () => clearTimeout(debounceTimer);
    } else {
      setCronError(null);
      setCronNextRuns([]);
    }
  }, [scheduleType, scheduleConfig]);

  const handleEnabledChange = (checked: boolean) => {
    if (checked) {
      onChange(value ?? DEFAULT_SCHEDULE);
    } else {
      // Unchecking clears the schedule entirely; on save this is sent as `null`,
      // which removes the underlying scheduled_jobs row.
      onChange(null);
    }
  };

  const handleScheduleTypeChange = (newType: string) => {
    const type = newType as ScheduleType;
    onChange({
      enabled: isEnabled,
      scheduleType: type,
      scheduleConfig: getDefaultConfigForType(type),
    });
  };

  const updateSimpleConfig = (updates: Partial<SimpleScheduleConfig>) => {
    const current = (scheduleConfig as SimpleScheduleConfig) ?? { time: '09:00', timezone: 'America/New_York' };
    onChange({
      enabled: isEnabled,
      scheduleType: 'simple',
      scheduleConfig: { ...current, ...updates },
    });
  };

  const updateCronConfig = (updates: Partial<CronScheduleConfig>) => {
    const current = (scheduleConfig as CronScheduleConfig) ?? { pattern: '0 9 * * *', timezone: 'America/New_York' };
    onChange({
      enabled: isEnabled,
      scheduleType: 'cron',
      scheduleConfig: { ...current, ...updates },
    });
  };

  const updateIntervalConfig = (intervalMs: number) => {
    onChange({
      enabled: isEnabled,
      scheduleType: 'interval',
      scheduleConfig: { intervalMs },
    });
  };

  const renderSimpleConfig = () => {
    const config = scheduleConfig as SimpleScheduleConfig | undefined;
    const time = config?.time ?? '09:00';
    const timeValid = /^([01]\d|2[0-3]):[0-5]\d$/.test(time);
    return (
      <Flex direction="column" gap="md">
        <TextField
          label="Time (HH:MM, 24-hour)"
          value={time}
          onChange={(v) => updateSimpleConfig({ time: v })}
          placeholder="14:30"
          description="Use 24-hour format, e.g. 09:00 or 14:30"
          isInvalid={!timeValid}
          errorMessage={timeValid ? undefined : 'Use HH:MM in 24-hour format (00:00 – 23:59)'}
        />
        <Select
          label="Timezone"
          items={COMMON_TIMEZONES}
          selectedKey={config?.timezone ?? 'America/New_York'}
          onSelectionChange={(key) => updateSimpleConfig({ timezone: key as string })}
        />
      </Flex>
    );
  };

  const renderCronConfig = () => {
    const config = scheduleConfig as CronScheduleConfig | undefined;
    return (
      <Flex direction="column" gap="md">
        <Box>
          <TextField
            label="Cron Pattern"
            value={config?.pattern ?? '0 9 * * *'}
            onChange={(v) => updateCronConfig({ pattern: v })}
            description="Standard cron expression (minute hour day month weekday)"
            isInvalid={!!cronError}
            errorMessage={cronError ?? undefined}
          />
          {cronNextRuns.length > 0 && (
            <Box className="mt-2 p-2 bg-muted rounded text-sm">
              <Text size="xs" weight="medium" className="text-muted-foreground mb-1">
                Next runs:
              </Text>
              <ul className="list-disc list-inside text-xs text-muted-foreground">
                {cronNextRuns.slice(0, 3).map((run, i) => (
                  <li key={i}>{new Date(run).toLocaleString()}</li>
                ))}
              </ul>
            </Box>
          )}
        </Box>
        <Select
          label="Timezone"
          items={COMMON_TIMEZONES}
          selectedKey={config?.timezone ?? 'America/New_York'}
          onSelectionChange={(key) => updateCronConfig({ timezone: key as string })}
        />
      </Flex>
    );
  };

  const renderIntervalConfig = () => {
    const config = scheduleConfig as IntervalScheduleConfig | undefined;
    const intervalMinutes = Math.round((config?.intervalMs ?? 3600000) / 60000);
    return (
      <NumberField
        label="Interval (minutes)"
        value={intervalMinutes}
        onChange={(v) => updateIntervalConfig((v ?? 60) * 60000)}
        minValue={1}
        maxValue={10080} // 1 week
        description="Time between runs in minutes"
      />
    );
  };

  return (
    <Flex direction="column" gap="md">
      <Checkbox
        isSelected={isEnabled}
        onChange={handleEnabledChange}
        label="Enable scheduled runs"
      />

      {isEnabled && (
        <>
          <Select
            label="Schedule Type"
            items={SCHEDULE_TYPE_OPTIONS}
            selectedKey={scheduleType}
            onSelectionChange={(key) => key && handleScheduleTypeChange(key as string)}
          />

          {scheduleType === 'simple' && renderSimpleConfig()}
          {scheduleType === 'cron' && renderCronConfig()}
          {scheduleType === 'interval' && renderIntervalConfig()}
        </>
      )}
    </Flex>
  );
};
